import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Badge
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import axios from 'axios';
import toastr from 'toastr';
import "toastr/build/toastr.min.css";

const CategoriesList = () => {
  // Sayfa Başlığı
  document.title = "Kategori Yönetimi | Subsea Admin";

  // --- API Yapılandırması ---
  const API_URL = "https://subseaapi.stechomeyazilim.info/Categories";

  // --- State Tanımları ---
  const [categories, setCategories] = useState([]);
  const [categoryEdit, setCategoryEdit] = useState(null);
  
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // --- Verileri Çekme ---
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // OData kullanıyorsan $expand ile parent ismini de çekebilirsin ama 
      // şimdilik düz çekip ID eşleşmesi yapacağız.
      const response = await axios.get(API_URL);
      const data = Array.isArray(response.data.value) ? response.data.value : response.data;
      setCategories(data);
    } catch (error) {
      console.error("Kategoriler yüklenemedi:", error);
      toastr.error("Veri çekme hatası.");
    }
  };

  // --- Formik Yapılandırması ---
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      Name: (categoryEdit && categoryEdit.Name) || "",
      Slug: (categoryEdit && categoryEdit.Slug) || "",
      Type: (categoryEdit && categoryEdit.Type) || "blog", // Varsayılan tip
      ParentID: (categoryEdit && categoryEdit.ParentID) || "", // 0 veya null
      SortOrder: (categoryEdit && categoryEdit.SortOrder) || 0,
    },
    validationSchema: Yup.object({
      Name: Yup.string().required("Kategori adı zorunludur"),
      Slug: Yup.string().required("Slug (URL yapısı) zorunludur"),
      Type: Yup.string().required("Tip zorunludur"),
    }),
    onSubmit: async (values) => {
      // API Payload Hazırlama
      const payload = {
        Name: values.Name,
        Slug: values.Slug,
        Type: values.Type,
        SortOrder: parseInt(values.SortOrder),
        // ParentID boş string ise null gönder, yoksa int gönder
        ParentID: values.ParentID ? parseInt(values.ParentID) : null,
      };

      try {
        if (isEdit && categoryEdit) {
            // GÜNCELLEME (PUT)
            await axios.put(`${API_URL}(${categoryEdit.ID})`, payload);
            toastr.success("Kategori güncellendi.");
        } else {
            // EKLEME (POST)
            await axios.post(API_URL, payload);
            toastr.success("Kategori eklendi.");
        }
        
        setModal(false);
        fetchCategories(); // Listeyi yenile
        validation.resetForm();
      } catch (error) {
        console.error("Kaydetme hatası:", error);
        toastr.error("İşlem başarısız (Slug benzersiz olmalı).");
      }
    },
  });

  // --- Tablo Kolonları ---
  const columns = useMemo(() => [
    {
      Header: "#",
      accessor: "ID",
      width: 50,
    },
    {
      Header: "Kategori Adı",
      accessor: "Name",
      filterable: true,
      Cell: (cellProps) => (
        <span className="fw-bold font-size-14">{cellProps.value}</span>
      )
    },
    {
      Header: "Tip",
      accessor: "Type",
      filterable: true,
      Cell: (cellProps) => {
          // Tipine göre renk verelim
          let color = "primary";
          if (cellProps.value === "blog") color = "info";
          if (cellProps.value === "listing") color = "warning";
          return <Badge color={color}>{cellProps.value}</Badge>
      }
    },
    {
      Header: "URL (Slug)",
      accessor: "Slug",
      Cell: (cellProps) => <span className="text-muted font-size-12">/{cellProps.value}</span>
    },
    {
      Header: "Üst Kategori",
      accessor: "ParentID",
      Cell: (cellProps) => {
          if(!cellProps.value) return "-";
          // Parent ID'ye karşılık gelen ismi listeden bul
          const parent = categories.find(c => c.ID === cellProps.value);
          return parent ? parent.Name : cellProps.value;
      }
    },
    {
      Header: "Sıra",
      accessor: "SortOrder",
    },
    {
      Header: "İşlemler",
      Cell: (cellProps) => (
        <div className="d-flex gap-2">
          <button 
            className="btn btn-sm btn-soft-primary" 
            onClick={() => handleEditClick(cellProps.row.original)}
          >
            <i className="mdi mdi-pencil font-size-14" />
          </button>
          <button 
            className="btn btn-sm btn-soft-danger" 
            onClick={() => handleDeleteClick(cellProps.row.original)}
          >
            <i className="mdi mdi-delete font-size-14" />
          </button>
        </div>
      ),
    },
  ], [categories]);

  // --- Helper Fonksiyonlar ---
  const toggle = () => {
    setModal(!modal);
    if (!modal && !isEdit) {
        validation.resetForm();
    }
  };

  const handleEditClick = (data) => {
    setCategoryEdit(data);
    setIsEdit(true);
    setModal(true);
  };

  const handleDeleteClick = (data) => {
    setCategoryToDelete(data);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
        try {
            await axios.delete(`${API_URL}(${categoryToDelete.ID})`);
            toastr.success("Kategori silindi.");
            fetchCategories();
        } catch (error) {
            toastr.error("Silme başarısız. Alt kategorisi olanlar silinemez.");
        }
    }
    setDeleteModal(false);
  };

  // İsim yazıldığında Slug'ı otomatik oluşturma (Opsiyonel Helper)
  const handleNameChange = (e) => {
      validation.handleChange(e);
      // Eğer yeni ekleme yapılıyorsa ve slug henüz elle değiştirilmediyse otomatik oluştur
      if (!isEdit && !validation.touched.Slug) {
          const slug = e.target.value
            .toLowerCase()
            .replace(/ /g, '-')
            .replace(/[^\w-]+/g, '');
          validation.setFieldValue('Slug', slug);
      }
  };

  return (
    <React.Fragment>
      <DeleteModal 
        show={deleteModal} 
        onDeleteClick={confirmDelete} 
        onCloseClick={() => setDeleteModal(false)} 
      />
      
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Kategoriler" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer 
                    columns={columns} 
                    data={categories} 
                    isGlobalFilter={true} 
                    isAddSubCat={true} // + Butonu görünmesi için (TableContainer prop'una bağlı)
                    handleOrderClicks={() => { 
                        setCategoryEdit(null); 
                        setIsEdit(false); 
                        setModal(true); 
                        validation.resetForm();
                    }} 
                    customPageSize={10} 
                  />

                  {/* --- Modal Form --- */}
                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle}>
                        {isEdit ? "Kategori Düzenle" : "Yeni Kategori Ekle"}
                    </ModalHeader>
                    <ModalBody>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label>Kategori Adı</Label>
                          <Input
                            name="Name"
                            type="text"
                            placeholder="Örn: Teknoloji"
                            onChange={handleNameChange} // Özel handler
                            onBlur={validation.handleBlur}
                            value={validation.values.Name}
                            invalid={validation.touched.Name && validation.errors.Name ? true : false}
                          />
                          {validation.touched.Name && validation.errors.Name ? (
                            <FormFeedback>{validation.errors.Name}</FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label>Slug (URL Yolu)</Label>
                          <Input
                            name="Slug"
                            type="text"
                            placeholder="Örn: teknoloji"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.Slug}
                            invalid={validation.touched.Slug && validation.errors.Slug ? true : false}
                          />
                          <small className="text-muted">Benzersiz olmalıdır.</small>
                          {validation.touched.Slug && validation.errors.Slug ? (
                            <FormFeedback>{validation.errors.Slug}</FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label>Kategori Tipi</Label>
                          <Input 
                            type="select" 
                            name="Type" 
                            value={validation.values.Type} 
                            onChange={validation.handleChange}
                          >
                            <option value="blog">Blog</option>
                            <option value="listing">İlan (Listing)</option>
                            <option value="equipment">Ekipman</option>
                            <option value="service">Hizmet</option>
                          </Input>
                        </div>

                        <div className="mb-3">
                          <Label>Üst Kategori (Parent)</Label>
                          <Input 
                            type="select" 
                            name="ParentID" 
                            value={validation.values.ParentID} 
                            onChange={validation.handleChange}
                          >
                            <option value="">Yok (Ana Kategori)</option>
                            {categories
                                // Kendisini parent olarak seçemesin (Edit modunda)
                                .filter(c => !isEdit || c.ID !== categoryEdit.ID)
                                .map((cat) => (
                                    <option key={cat.ID} value={cat.ID}>
                                        {cat.Name} ({cat.Type})
                                    </option>
                            ))}
                          </Input>
                        </div>

                        <div className="mb-3">
                          <Label>Sıralama (Sort Order)</Label>
                          <Input
                            name="SortOrder"
                            type="number"
                            onChange={validation.handleChange}
                            value={validation.values.SortOrder}
                          />
                        </div>

                        <div className="text-end">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={toggle}
                          >
                            İptal
                          </button>
                          <button
                            type="submit"
                            className="btn btn-success"
                          >
                            Kaydet
                          </button>
                        </div>
                      </Form>
                    </ModalBody>
                  </Modal>

                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(CategoriesList);